import cn from '@sindresorhus/class-names';
import {usePathname} from 'next/navigation';
import {ComponentProps, memo, useCallback} from 'react';

import {AppsResponse} from '@web-catalog/types';

import {useSafeOmicronFlag} from '@web-catalog/store';

import {PaginationComponent} from '@web-catalog/ui-kit/Pagination';

import {removeDuplicatedSlashes} from '@web-catalog/utils';

import styles from './AppsWithPagination.module.scss';
import {AppsList} from './parts';

type Props = {
  appsResponse?: AppsResponse;
  search?: string;
  pageNumber: number;
  listName: string;
  onPageChange: (page: number, pageSize: number) => void;
  logCustomAnalyticsOnClick?: ComponentProps<
    typeof AppsList
  >['logCustomAnalyticsOnClick'];
  allowAds?: boolean;
  className?: string;
  prefetch?: boolean;
};

export const AppsWithPagination = memo<Props>(
  ({
    appsResponse,
    search,
    pageNumber,
    listName,
    allowAds,
    className,
    prefetch,
    onPageChange,
    logCustomAnalyticsOnClick,
  }) => {
    const path = usePathname();

    const appDataForDisplay =
      !useSafeOmicronFlag('IS_SHOW_APP_CARD_RATING') && appsResponse
        ? {
            ...appsResponse,
            body: {
              ...appsResponse?.body,
              content: appsResponse?.body.content.map((app) => ({
                ...app,
                averageUserRating: null,
                totalRatings: null,
              })),
            },
          }
        : appsResponse;

    const appsCount = appsResponse?.body.pageSize ?? 0;
    const appsPageSize = appsResponse?.body.pageSize ?? 0;
    const appsTotalElements = appsResponse?.body.totalElements ?? 0;

    const customHref = useCallback(
      (page: number) => {
        if (!path) {
          return '';
        }

        const dynamicPage = page === 1 ? '' : `page-${page}`;

        if (path?.includes('page-')) {
          return removeDuplicatedSlashes(
            path.replace(/page-\d+/g, dynamicPage),
          );
        }

        return removeDuplicatedSlashes(path + '/' + dynamicPage);
      },
      [path],
    );

    return (
      <div className={cn(styles.appsWrapper, className)}>
        <div
          className={cn(styles.apps, {
            [styles.appsEmpty]: appsCount === 0 && !search,
            [styles.appsSearchEmpty]: search && appsCount === 0,
          })}
        >
          {!appsCount && <p className={styles.empty}>Ничего не найдено</p>}

          {!!appsCount && (
            <>
              <ul className={styles.list} key="appslist" data-testid="appslist">
                <AppsList
                  appsData={appDataForDisplay}
                  listName={listName}
                  allowAds={allowAds}
                  logCustomAnalyticsOnClick={logCustomAnalyticsOnClick}
                />
              </ul>

              {appsTotalElements > appsPageSize && (
                <hr className={styles.appsDelimiter} />
              )}
            </>
          )}

          <PaginationComponent
            current={pageNumber}
            pageSize={appsPageSize}
            total={appsTotalElements}
            onChange={onPageChange}
            customHref={customHref}
            data-testid=""
            prefetch={prefetch}
          />
        </div>
      </div>
    );
  },
);

AppsWithPagination.displayName = 'AppsWithPagination';
